import React, { useEffect, useMemo, useState } from 'react'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { debounce } from '@mui/material/utils'
import { useSearchParams } from 'react-router-dom'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'

const SearchParam = ({ param, placeholder = 'Search...', debounceMs = 500, maxWidth = 300 }) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const paramValue = searchParams.get(param) ?? ''

  const [inputValue, setInputValue] = useState(paramValue)

  // Keep local state synced with URL changes
  useEffect(() => {
    setInputValue(paramValue)
  }, [paramValue])

  const updateSearchParam = useMemo(
    () =>
      debounce((value) => {
        const params = new URLSearchParams(window.location.search)

        if (value?.trim()) {
          params.set(param, value.trim())
        } else {
          params.delete(param)
        }

        setSearchParams(params, { replace: true })
      }, debounceMs),
    [debounceMs, param, setSearchParams],
  )

  useEffect(() => {
    return () => {
      updateSearchParam.clear()
    }
  }, [updateSearchParam])

  const handleChange = (e) => {
    const value = e.target.value

    setInputValue(value)
    updateSearchParam(value)
  }

  const handleClear = () => {
    updateSearchParam.clear()

    setInputValue('')

    const params = new URLSearchParams(searchParams)

    params.delete(param)

    setSearchParams(params, { replace: true })
  }

  return (
    <TextField
      size='small'
      value={inputValue}
      onChange={handleChange}
      placeholder={placeholder}
      sx={{
        width: '100%',
        maxWidth,
        backgroundColor: 'white',
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon fontSize='small' />
          </InputAdornment>
        ),
        endAdornment: inputValue ? (
          <InputAdornment position='end'>
            <IconButton size='small' onClick={handleClear} edge='end'>
              <ClearIcon fontSize='small' />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  )
}

export default SearchParam
